import { ApolloClient, ApolloLink, HttpLink, InMemoryCacheConfig } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { sha256 } from 'crypto-hash'
import Cookies from 'universal-cookie'

import { ServiceFilterPairs, catalogCacheConfig } from '@syconium/weeping-figs'

import catalogSupergraphCodegenResults from '../../__generated__/graphql/catalog/graphql'

import { cookieKeys } from './Cookies.config'
import { headerKeys } from './Headers.config'
import { LocalizationAttributes } from './Localization.config'
import { PreviewDirectives } from './PreviewDirectives.config'

export const magnoliaSupergraphGraphqlCacheConfig: InMemoryCacheConfig = {
  ...catalogCacheConfig,

  possibleTypes: catalogSupergraphCodegenResults.possibleTypes,

  typePolicies: {
    ...(catalogCacheConfig.typePolicies ?? {}),
  },
}

const shopName = process.env.NEXT_PUBLIC_SHOPIFY_SHOP_NAME
const isLocalDev = process.env.NEXT_PUBLIC_FIGS_ENV === 'local'
const isServer = typeof window === 'undefined'

// Use relative URLs for clientside api calls.
const clusterBaseUrl = isServer ? process.env.INTERNAL_CLUSTER_BASE_URL : ''

// The WAF causes 403s with requests from localdev if we don't proxy and sanitize them.
// Always use the assigned cluster target for localdev since that will be localhost and get proxied to CDN after sanitization.
const serverSideCdnTarget = isLocalDev ? clusterBaseUrl : process.env.NEXT_PUBLIC_CDN_BASE_URL
const cdnBaseUrl = isServer ? serverSideCdnTarget : ''

const getAuthLink = (authToken: string | null | undefined) => {
  return setContext((_operation, { headers }) => {
    const clientSideCookies = new Cookies()
    const authorization =
      clientSideCookies.get(cookieKeys.authToken.key) ?? authToken ?? headers?.authorization
    return {
      headers: {
        ...(headers ?? {}),
        ...(authorization ? { authorization } : {}),
      },
    }
  })
}

const getLocalizationLink = (localization: LocalizationAttributes) => {
  return setContext((_operation, { headers }) => {
    const clientSideCookies = new Cookies()
    return {
      headers: {
        ...(headers ?? {}),
        [headerKeys.region]: clientSideCookies.get(cookieKeys.region.key) ?? localization.region.id,
        [headerKeys.locale]: clientSideCookies.get(cookieKeys.locale.key) ?? localization.locale,
        [headerKeys.currency]:
          clientSideCookies.get(cookieKeys.currency.key) ?? localization.currency,
        ...(shopName && {
          [headerKeys.shopName]: shopName,
        }),
      },
    }
  })
}

const getServiceFiltersLink = (previewDirectives: PreviewDirectives) => {
  return setContext(
    (
      _operation,
      {
        isPreview: alreadyIsPreview,
        includeUnavailable: alreadyIncludeUnavailable,
        customerTier: alreadyCustomerTier,
        launchTags: alreadyLaunchTags,
        releaseDate: alreadyReleaseDate,
        headers,
      }
    ) => {
      const includeUnavailable =
        alreadyIncludeUnavailable ?? previewDirectives.includeUnavailableProducts
      const isPreview = alreadyIsPreview ?? previewDirectives.includeUnpublishedCmsContent
      const customerTier = alreadyCustomerTier ?? 'web'
      const launchTags =
        alreadyLaunchTags ?? previewDirectives.includeUnpublishedProductsForLaunchTags
      const releaseDate = alreadyReleaseDate ?? previewDirectives.releaseDate

      const serviceFilters = ServiceFilterPairs.buildServiceFilterHeader({
        includeUnavailable,
        isPreview,
        launchTags,
        releaseDate,
        customerTier,
      })
      return {
        includeUnavailable,
        isPreview,
        customerTier,
        launchTags,
        releaseDate,
        headers: {
          ...(headers ?? {}),
          ...(serviceFilters ? { [headerKeys.serviceFilters]: serviceFilters } : {}),
        },
      }
    }
  )
}

const getPersistedQueryLink = () => {
  return createPersistedQueryLink({ sha256, useGETForHashedQueries: true })
}

const getHttpLink = (graphPathname: string, isPersonalized: boolean) => {
  return new HttpLink({
    uri: `${isPersonalized ? clusterBaseUrl : cdnBaseUrl}${graphPathname}`,
  })
}

export const getCredentialsLink = (serializedCookieString: string | null | undefined) => {
  return setContext((_operation, { headers }) => {
    return {
      headers: {
        ...headers,
        ...(serializedCookieString ? { cookie: serializedCookieString } : {}),
      },
    }
  })
}

export const getErrorLink = () => {
  return new ErrorLink(({ operation, error }) => {
    const { onOperationError } = operation.getContext()
    if (onOperationError) {
      onOperationError(error)
      return
    }
  })
}

type GetGraphqlClientLinksProps = {
  serializedCookieString: string | null | undefined
  graphPathname: string
  localization: LocalizationAttributes
  previewDirectives: PreviewDirectives
} & (
  | {
      type: 'personalized'
      authToken: string | null | undefined
    }
  | {
      type: 'cacheOptimized'
      authToken?: string | null | undefined
    }
)

export const getGraphqlClientLinks = ({
  type,
  authToken,
  graphPathname,
  localization,
  previewDirectives,
  serializedCookieString,
}: GetGraphqlClientLinksProps) => {
  // Set `Authorization` header on request if we want personalized responses
  const authLinks = ApolloLink.split(
    () => type === 'personalized',
    getAuthLink(authToken),
    undefined
  )

  // Set `cookie` header on request if `serializedCookieString` is provided. This is necessary to forward cookies from the incoming server-side request when executing GraphQL operations in a server-side context
  const credentialLinks = ApolloLink.split(
    () => Boolean(serializedCookieString),
    getCredentialsLink(serializedCookieString),
    undefined
  )

  return (
    authLinks
      .concat(credentialLinks)
      .concat(getErrorLink())
      // Append non-conditional links
      .concat(getLocalizationLink(localization))
      .concat(getServiceFiltersLink(previewDirectives))
      .concat(getPersistedQueryLink())
      .concat(getHttpLink(graphPathname, type === 'personalized'))
  )
}

export const graphqlClientsDefaultOptions: ApolloClient.DefaultOptions.Input = {
  watchQuery: {
    errorPolicy: 'none',
  },
  query: {
    errorPolicy: 'all',
  },
  mutate: {
    errorPolicy: 'none',
  },
}

export type GraphQlClients = 'cache-optimized' | 'personalized'
